import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/utils/api';

const Employees = ({ user }) => {
  const [employees, setEmployees] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  
  // Quick add states
  const [showQuickAddType, setShowQuickAddType] = useState(false);
  const [showQuickAddCentre, setShowQuickAddCentre] = useState(false);
  const [showQuickAddCycle, setShowQuickAddCycle] = useState(false);
  const [showQuickAddEmpType, setShowQuickAddEmpType] = useState(false);
  const [quickTypeData, setQuickTypeData] = useState({ name: '', description: '' });
  const [quickCentreData, setQuickCentreData] = useState({ name: '', code: '', description: '' });
  const [quickCycleData, setQuickCycleData] = useState({ name: '' });
  const [quickEmpTypeData, setQuickEmpTypeData] = useState({ name: '' });
  const [employmentTypeOptions, setEmploymentTypeOptions] = useState(['Payroll', 'Commission']);
  const [salaryCycles, setSalaryCycles] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCostCentre, setFilterCostCentre] = useState('');
  const [filterEmpType, setFilterEmpType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    employee_type_id: '',
    cost_centre_id: '',
    joining_date: '',
    basic_salary: '',
    hra: '',
    da: '',
    conv: '',
    gender: 'Male',
    shift_hours: '8',
    status: 'Active',
    account_number: '',
    ifsc_code: '',
    pan_number: '',
    bank_name: '',
    branch_name: '',
    aadhar_number: '',
    uan_number: '',
    deduction_type: '',
    statutory_deductions: false,
    ded_pf: false,
    ded_esic: false,
    ded_ptax: false,
    ded_mlwf: false,
    ded_tds: false,
    pf_percentage: '',
    esic_percentage: '',
    mlwf_amount: '',
    ptax_amount: '',
    tds_percentage: '',
    salary_cycle: '1st to End of Month',
    employment_type: 'Payroll',
    leave_tracking: false,
    casual_leave: '12',
    sick_leave: '6',
    earned_leave: '15',
    annual_leave: '12',
    maternity_paternity_leave: '0',
  });
  const [typeFormData, setTypeFormData] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, typeRes, ccRes, cycleRes, empTypeRes] = await Promise.all([
        api.get('/employees'),
        api.get('/employee-types'),
        api.get('/cost-centres'),
        api.get('/salary-cycles'),
        api.get('/employment-type-options').catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data);
      setEmployeeTypes(typeRes.data);
      setCostCentres(ccRes.data);
      setSalaryCycles(cycleRes.data);
      const defaults = ['Payroll', 'Commission'];
      const custom = (empTypeRes.data || []).map(t => t.name).filter(n => !defaults.includes(n));
      // Also include types actually used by employees
      const usedTypes = [...new Set((empRes.data || []).map(e => e.employment_type).filter(Boolean))];
      const allTypes = [...new Set([...defaults, ...custom, ...usedTypes])];
      setEmploymentTypeOptions(allTypes);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.account_number && formData.account_number !== formData.confirm_account_number) {
      toast.error('Account numbers do not match');
      return;
    }
    try {
      const payload = {
        ...formData,
        basic_salary: parseFloat(formData.basic_salary) || 0,
        hra: parseFloat(formData.hra) || 0,
        da: parseFloat(formData.da) || 0,
        conv: parseFloat(formData.conv) || 0,
        gender: formData.gender,
        shift_hours: parseInt(formData.shift_hours) || 8,
        statutory_deductions: formData.ded_pf || formData.ded_esic || formData.ded_ptax || formData.ded_mlwf || formData.ded_tds,
        ded_pf: formData.ded_pf,
        ded_esic: formData.ded_esic,
        ded_ptax: formData.ded_ptax,
        ded_mlwf: formData.ded_mlwf,
        ded_tds: formData.ded_tds,
        pf_percentage: formData.ded_pf ? 12 : 0,
        esic_percentage: formData.ded_esic ? 0.75 : 0,
        mlwf_amount: 0,
        ptax_amount: 0,
        tds_percentage: formData.ded_tds ? parseFloat(formData.tds_percentage) || 0 : 0,
        salary_cycle: formData.salary_cycle,
        employment_type: formData.employment_type,
        casual_leave: parseInt(formData.casual_leave) || 0,
        sick_leave: parseInt(formData.sick_leave) || 0,
        earned_leave: parseInt(formData.earned_leave) || 0,
        annual_leave: parseInt(formData.annual_leave) || 0,
        maternity_paternity_leave: parseInt(formData.maternity_paternity_leave) || 0,
      };
      delete payload.confirm_account_number;
      
      if (editingEmployee) {
        await api.put(`/employees/${editingEmployee.id}`, payload);
        toast.success('Employee updated successfully');
      } else {
        await api.post('/employees', payload);
        toast.success('Employee created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      await api.post('/employee-types', typeFormData);
      toast.success('Designation created successfully');
      setTypeDialogOpen(false);
      setTypeFormData({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleQuickAddType = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/employee-types', quickTypeData);
      toast.success('Designation created!');
      setQuickTypeData({ name: '', description: '' });
      setShowQuickAddType(false);
      await fetchData();
      setFormData({ ...formData, employee_type_id: response.data.id });
    } catch (error) {
      toast.error('Failed to create designation');
    }
  };

  const handleQuickAddCentre = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/cost-centres', quickCentreData);
      toast.success('Cost centre created!');
      setQuickCentreData({ name: '', code: '', description: '' });
      setShowQuickAddCentre(false);
      await fetchData();
      setFormData({ ...formData, cost_centre_id: response.data.id });
    } catch (error) {
      toast.error('Failed to create cost centre');
    }
  };

  const handleQuickAddCycle = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/salary-cycles', quickCycleData);
      toast.success('Salary cycle created!');
      setQuickCycleData({ name: '' });
      setShowQuickAddCycle(false);
      await fetchData();
      setFormData({ ...formData, salary_cycle: response.data.name });
    } catch (error) {
      toast.error('Failed to create salary cycle');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success('Employee deleted successfully');
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      phone: employee.phone,
      employee_type_id: employee.employee_type_id,
      cost_centre_id: employee.cost_centre_id || '',
      joining_date: employee.joining_date,
      basic_salary: employee.basic_salary,
      hra: String(employee.hra || 0),
      da: String(employee.da || 0),
      conv: String(employee.conv || 0),
      status: employee.status || 'Active',
      account_number: employee.account_number || '',
      confirm_account_number: employee.account_number || '',
      ifsc_code: employee.ifsc_code || '',
      pan_number: employee.pan_number || '',
      bank_name: employee.bank_name || '',
      branch_name: employee.branch_name || '',
      aadhar_number: employee.aadhar_number || '',
      uan_number: employee.uan_number || '',
      deduction_type: employee.deduction_type || '',
      gender: employee.gender || 'Male',
      shift_hours: String(employee.shift_hours || '8'),
      statutory_deductions: employee.statutory_deductions || false,
      ded_pf: employee.ded_pf || false,
      ded_esic: employee.ded_esic || false,
      ded_ptax: employee.ded_ptax || false,
      ded_mlwf: employee.ded_mlwf || false,
      ded_tds: employee.ded_tds || false,
      pf_percentage: String(employee.pf_percentage || ''),
      esic_percentage: String(employee.esic_percentage || ''),
      mlwf_amount: String(employee.mlwf_amount || ''),
      ptax_amount: String(employee.ptax_amount || ''),
      tds_percentage: String(employee.tds_percentage || ''),
      salary_cycle: employee.salary_cycle || '1st to End of Month',
      employment_type: employee.employment_type || 'Payroll',
      leave_tracking: employee.leave_tracking || false,
      casual_leave: String(employee.casual_leave ?? 12),
      sick_leave: String(employee.sick_leave ?? 6),
      earned_leave: String(employee.earned_leave ?? 15),
      annual_leave: String(employee.annual_leave ?? 12),
      maternity_paternity_leave: String(employee.maternity_paternity_leave ?? 0),
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      employee_type_id: '',
      cost_centre_id: '',
      joining_date: '',
      basic_salary: '',
      hra: '',
      da: '',
      conv: '',
      gender: 'Male',
      shift_hours: '8',
      account_number: '',
      ifsc_code: '',
      pan_number: '',
      deduction_type: '',
      statutory_deductions: false,
      ded_pf: false,
      ded_esic: false,
      ded_ptax: false,
      ded_mlwf: false,
      ded_tds: false,
      pf_percentage: '',
      esic_percentage: '',
      mlwf_amount: '',
      ptax_amount: '',
      tds_percentage: '',
      salary_cycle: '1st to End of Month',
      employment_type: 'Payroll',
    });
    setShowQuickAddType(false);
    setShowQuickAddCentre(false);
    setShowQuickAddCycle(false);
  };

  const exportEmployees = async () => {
    try {
      const response = await api.get('/export/employees', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employees.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Employees exported successfully');
    } catch (error) {
      toast.error('Failed to export employees');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Employees</h1>
          <p className="mt-2 text-base text-slate-600">Manage your workforce</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={exportEmployees} variant="outline" data-testid="export-employees-button">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-employee-type-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Designation
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Designation</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateType} className="space-y-4">
                <div>
                  <Label htmlFor="type-name">Designation Name</Label>
                  <Input
                    id="type-name"
                    value={typeFormData.name}
                    onChange={(e) => setTypeFormData({ ...typeFormData, name: e.target.value })}
                    required
                    data-testid="type-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="type-description">Description</Label>
                  <Input
                    id="type-description"
                    value={typeFormData.description}
                    onChange={(e) => setTypeFormData({ ...typeFormData, description: e.target.value })}
                    data-testid="type-description-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-type-button">Create Type</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              {user?.role !== 'CEO' && (
              <Button data-testid="add-employee-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Employee
              </Button>
              )}
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="employee-email-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="employee-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="employee_type">Designation</Label>
                    <div className="flex gap-2 items-center">
                      <select
                        id="employee_type"
                        value={formData.employee_type_id}
                        onChange={(e) => setFormData({ ...formData, employee_type_id: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                        required
                        data-testid="employee-type-select"
                      >
                        <option value="">Select designation</option>
                        {employeeTypes.map((type) => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowQuickAddType(!showQuickAddType)}
                        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap"
                      >
                        + Add
                      </button>
                    </div>
                    {showQuickAddType && (
                      <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                        <p className="text-xs font-semibold text-indigo-900 mb-2">Quick Add Type</p>
                        <Input
                          placeholder="Type name"
                          value={quickTypeData.name}
                          onChange={(e) => setQuickTypeData({ ...quickTypeData, name: e.target.value })}
                          className="mb-2"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleQuickAddType}
                            className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowQuickAddType(false)}
                            className="text-xs text-slate-600 px-3 py-1 rounded hover:bg-slate-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cost_centre">Cost Centre</Label>
                    <div className="flex gap-2 items-center">
                      <select
                        id="cost_centre"
                        value={formData.cost_centre_id}
                        onChange={(e) => setFormData({ ...formData, cost_centre_id: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                        data-testid="cost-centre-select"
                      >
                        <option value="">Select cost centre</option>
                        {costCentres.map((cc) => (
                          <option key={cc.id} value={cc.id}>{cc.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowQuickAddCentre(!showQuickAddCentre)}
                        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap"
                      >
                        + Add
                      </button>
                    </div>
                    {showQuickAddCentre && (
                      <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                        <p className="text-xs font-semibold text-indigo-900 mb-2">Quick Add Cost Centre</p>
                        <Input
                          placeholder="Centre name"
                          value={quickCentreData.name}
                          onChange={(e) => setQuickCentreData({ ...quickCentreData, name: e.target.value })}
                          className="mb-2"
                        />
                        <Input
                          placeholder="Code (optional)"
                          value={quickCentreData.code}
                          onChange={(e) => setQuickCentreData({ ...quickCentreData, code: e.target.value })}
                          className="mb-2"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleQuickAddCentre}
                            className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowQuickAddCentre(false)}
                            className="text-xs text-slate-600 px-3 py-1 rounded hover:bg-slate-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="employment_type">Type Of Employee</Label>
                    <div className="flex gap-2 items-center">
                      <select
                        id="employment_type"
                        value={formData.employment_type}
                        onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                        data-testid="employment-type-select"
                      >
                        {employmentTypeOptions.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowQuickAddEmpType(!showQuickAddEmpType)}
                        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap"
                      >
                        + Add
                      </button>
                    </div>
                    {showQuickAddEmpType && (
                      <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                        <p className="text-xs font-semibold text-indigo-900 mb-2">Quick Add Employee Type</p>
                        <Input
                          placeholder="Type name (e.g. Contract)"
                          value={quickEmpTypeData.name}
                          onChange={(e) => setQuickEmpTypeData({ ...quickEmpTypeData, name: e.target.value })}
                          className="mb-2"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              if (!quickEmpTypeData.name.trim()) return;
                              try {
                                await api.post('/employment-type-options', { name: quickEmpTypeData.name.trim() });
                                setEmploymentTypeOptions(prev => [...prev, quickEmpTypeData.name.trim()]);
                                setFormData({ ...formData, employment_type: quickEmpTypeData.name.trim() });
                                setQuickEmpTypeData({ name: '' });
                                setShowQuickAddEmpType(false);
                                toast.success('Employee type added');
                              } catch (err) {
                                toast.error('Failed to add type');
                              }
                            }}
                            className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowQuickAddEmpType(false)}
                            className="text-xs text-slate-600 px-3 py-1 rounded hover:bg-slate-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="joining_date">Joining Date</Label>
                    <Input
                      id="joining_date"
                      type="date"
                      value={formData.joining_date}
                      onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                      required
                      data-testid="joining-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender</Label>
                    <select
                      id="gender"
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                      data-testid="gender-select"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="shift_hours">Shift Hours</Label>
                    <select
                      id="shift_hours"
                      value={formData.shift_hours}
                      onChange={(e) => setFormData({ ...formData, shift_hours: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                      data-testid="shift-hours-select"
                    >
                      <option value="8">8 Hours</option>
                      <option value="9">9 Hours</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                      required
                      data-testid="employee-status-select"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="On Annual Leave">On Annual Leave</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <div>
                    <Label htmlFor="basic_salary">Basic Salary (₹)</Label>
                    <Input
                      id="basic_salary"
                      type="number"
                      step="0.01"
                      value={formData.basic_salary}
                      onChange={(e) => setFormData({ ...formData, basic_salary: e.target.value })}
                      required
                      data-testid="basic-salary-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hra">HRA (₹)</Label>
                    <Input
                      id="hra"
                      type="number"
                      step="0.01"
                      value={formData.hra}
                      onChange={(e) => setFormData({ ...formData, hra: e.target.value })}
                      placeholder="0"
                      data-testid="hra-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="da">DA (₹)</Label>
                    <Input
                      id="da"
                      type="number"
                      step="0.01"
                      value={formData.da}
                      onChange={(e) => setFormData({ ...formData, da: e.target.value })}
                      placeholder="0"
                      data-testid="da-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="conv">Conv (₹)</Label>
                    <Input
                      id="conv"
                      type="number"
                      step="0.01"
                      value={formData.conv}
                      onChange={(e) => setFormData({ ...formData, conv: e.target.value })}
                      placeholder="0"
                      data-testid="conv-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gross_salary">Gross Salary (₹)</Label>
                    <Input
                      id="gross_salary"
                      type="number"
                      value={Math.round((parseFloat(formData.basic_salary) || 0) + (parseFloat(formData.hra) || 0) + (parseFloat(formData.da) || 0) + (parseFloat(formData.conv) || 0))}
                      readOnly
                      className="bg-slate-100 font-semibold"
                      data-testid="gross-salary-output"
                    />
                    <p className="text-xs text-slate-500 mt-1">Basic + HRA + DA + Conv</p>
                  </div>
                </div>
                <div>
                  <Label htmlFor="salary_cycle">Salary Cycle</Label>
                    <div className="flex gap-2 items-center">
                      <select
                        id="salary_cycle"
                        value={formData.salary_cycle}
                        onChange={(e) => setFormData({ ...formData, salary_cycle: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                        required
                        data-testid="salary-cycle-select"
                      >
                        <option value="">Select cycle</option>
                        {salaryCycles.map((cycle) => (
                          <option key={cycle.id} value={cycle.name}>{cycle.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowQuickAddCycle(!showQuickAddCycle)}
                        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap"
                        data-testid="quick-add-cycle-btn"
                      >
                        + Add
                      </button>
                    </div>
                    {showQuickAddCycle && (
                      <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                        <p className="text-xs font-semibold text-indigo-900 mb-2">Quick Add Salary Cycle</p>
                        <Input
                          placeholder="e.g. 21st to 20th"
                          value={quickCycleData.name}
                          onChange={(e) => setQuickCycleData({ ...quickCycleData, name: e.target.value })}
                          className="mb-2"
                          data-testid="quick-cycle-name-input"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleQuickAddCycle}
                            className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                            data-testid="quick-cycle-save-btn"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowQuickAddCycle(false)}
                            className="text-xs text-slate-600 px-3 py-1 rounded hover:bg-slate-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="account_number">Account Number</Label>
                    <Input
                      id="account_number"
                      type="text"
                      autoComplete="off"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      onPaste={(e) => e.preventDefault()}
                      onCopy={(e) => e.preventDefault()}
                      placeholder="Enter account number"
                      data-testid="account-number-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_account_number">Re-enter Account Number</Label>
                    <Input
                      id="confirm_account_number"
                      type="password"
                      autoComplete="off"
                      value={formData.confirm_account_number || ''}
                      onChange={(e) => setFormData({ ...formData, confirm_account_number: e.target.value })}
                      onPaste={(e) => e.preventDefault()}
                      onCopy={(e) => e.preventDefault()}
                      placeholder="Re-enter account number"
                      data-testid="confirm-account-number-input"
                    />
                    {formData.account_number && formData.confirm_account_number && formData.account_number !== formData.confirm_account_number && (
                      <p className="text-xs text-rose-600 mt-1 font-medium">Account numbers do not match</p>
                    )}
                    {formData.account_number && formData.confirm_account_number && formData.account_number === formData.confirm_account_number && (
                      <p className="text-xs text-emerald-600 mt-1 font-medium">Account numbers match</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="ifsc_code">IFSC Code</Label>
                    <Input
                      id="ifsc_code"
                      value={formData.ifsc_code}
                      onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value })}
                      placeholder="Enter IFSC code"
                      data-testid="ifsc-code-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="bank_name">Bank Name</Label>
                    <select
                      id="bank_name"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                      data-testid="bank-name-input"
                    >
                      <option value="">Select bank</option>
                      <option value="State Bank of India">State Bank of India (SBI)</option>
                      <option value="Punjab National Bank">Punjab National Bank (PNB)</option>
                      <option value="Bank of Baroda">Bank of Baroda</option>
                      <option value="Bank of India">Bank of India</option>
                      <option value="Canara Bank">Canara Bank</option>
                      <option value="Union Bank of India">Union Bank of India</option>
                      <option value="Indian Bank">Indian Bank</option>
                      <option value="Central Bank of India">Central Bank of India</option>
                      <option value="Indian Overseas Bank">Indian Overseas Bank</option>
                      <option value="UCO Bank">UCO Bank</option>
                      <option value="Bank of Maharashtra">Bank of Maharashtra</option>
                      <option value="Punjab & Sind Bank">Punjab & Sind Bank</option>
                      <option value="HDFC Bank">HDFC Bank</option>
                      <option value="ICICI Bank">ICICI Bank</option>
                      <option value="Axis Bank">Axis Bank</option>
                      <option value="Kotak Mahindra Bank">Kotak Mahindra Bank</option>
                      <option value="IndusInd Bank">IndusInd Bank</option>
                      <option value="Yes Bank">Yes Bank</option>
                      <option value="IDBI Bank">IDBI Bank</option>
                      <option value="Federal Bank">Federal Bank</option>
                      <option value="South Indian Bank">South Indian Bank</option>
                      <option value="RBL Bank">RBL Bank</option>
                      <option value="Bandhan Bank">Bandhan Bank</option>
                      <option value="IDFC First Bank">IDFC First Bank</option>
                      <option value="Jammu & Kashmir Bank">Jammu & Kashmir Bank</option>
                      <option value="Karnataka Bank">Karnataka Bank</option>
                      <option value="Karur Vysya Bank">Karur Vysya Bank</option>
                      <option value="City Union Bank">City Union Bank</option>
                      <option value="Tamilnad Mercantile Bank">Tamilnad Mercantile Bank</option>
                      <option value="DCB Bank">DCB Bank</option>
                      <option value="Dhanlaxmi Bank">Dhanlaxmi Bank</option>
                      <option value="CSB Bank">CSB Bank</option>
                      <option value="Nainital Bank">Nainital Bank</option>
                      <option value="AU Small Finance Bank">AU Small Finance Bank</option>
                      <option value="Equitas Small Finance Bank">Equitas Small Finance Bank</option>
                      <option value="Ujjivan Small Finance Bank">Ujjivan Small Finance Bank</option>
                      <option value="Jana Small Finance Bank">Jana Small Finance Bank</option>
                      <option value="Suryoday Small Finance Bank">Suryoday Small Finance Bank</option>
                      <option value="India Post Payments Bank">India Post Payments Bank</option>
                      <option value="Paytm Payments Bank">Paytm Payments Bank</option>
                      <option value="Airtel Payments Bank">Airtel Payments Bank</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="branch_name">Branch Name</Label>
                    <Input
                      id="branch_name"
                      value={formData.branch_name}
                      onChange={(e) => setFormData({ ...formData, branch_name: e.target.value })}
                      placeholder="Enter branch name"
                      data-testid="branch-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="pan_number">PAN Number</Label>
                    <Input
                      id="pan_number"
                      value={formData.pan_number}
                      onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
                      data-testid="pan-number-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="aadhar_number">Aadhaar Number</Label>
                    <Input
                      id="aadhar_number"
                      value={formData.aadhar_number}
                      onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value })}
                      placeholder="12-digit Aadhaar"
                      data-testid="aadhar-number-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="uan_number">UAN Number</Label>
                    <Input
                      id="uan_number"
                      value={formData.uan_number}
                      onChange={(e) => setFormData({ ...formData, uan_number: e.target.value })}
                      placeholder="12-digit UAN"
                      data-testid="uan-number-input"
                    />
                  </div>
                </div>
                
                {/* Deductions — Checkboxes */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Type of Deductions</p>
                  <div className="grid grid-cols-5 gap-4 mb-4">
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="ded_pf" checked={formData.ded_pf} onChange={(e) => setFormData({ ...formData, ded_pf: e.target.checked, statutory_deductions: true })} className="h-4 w-4 rounded border-slate-300" data-testid="ded-pf-checkbox" />
                      <Label htmlFor="ded_pf" className="cursor-pointer text-sm">PF</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="ded_esic" checked={formData.ded_esic} onChange={(e) => setFormData({ ...formData, ded_esic: e.target.checked, statutory_deductions: true })} className="h-4 w-4 rounded border-slate-300" data-testid="ded-esic-checkbox" />
                      <Label htmlFor="ded_esic" className="cursor-pointer text-sm">ESIC</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="ded_ptax" checked={formData.ded_ptax} onChange={(e) => setFormData({ ...formData, ded_ptax: e.target.checked, statutory_deductions: true })} className="h-4 w-4 rounded border-slate-300" data-testid="ded-ptax-checkbox" />
                      <Label htmlFor="ded_ptax" className="cursor-pointer text-sm">PTAX</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="ded_mlwf" checked={formData.ded_mlwf} onChange={(e) => setFormData({ ...formData, ded_mlwf: e.target.checked, statutory_deductions: true })} className="h-4 w-4 rounded border-slate-300" data-testid="ded-mlwf-checkbox" />
                      <Label htmlFor="ded_mlwf" className="cursor-pointer text-sm">MLWF</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="ded_tds" checked={formData.ded_tds} onChange={(e) => setFormData({ ...formData, ded_tds: e.target.checked, statutory_deductions: true })} className="h-4 w-4 rounded border-slate-300" data-testid="ded-tds-checkbox" />
                      <Label htmlFor="ded_tds" className="cursor-pointer text-sm">TDS</Label>
                    </div>
                  </div>
                  
                  {/* Only show input fields for deductions that need manual values */}
                  {(formData.ded_pf || formData.ded_esic || formData.ded_ptax || formData.ded_mlwf || formData.ded_tds) && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {formData.ded_pf && (
                          <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                            <p className="text-xs font-semibold text-emerald-700">PF — Auto</p>
                            <p className="text-xs text-emerald-600 mt-1">12% of (Basic+DA), capped at ₹1,800</p>
                          </div>
                        )}
                        {formData.ded_esic && (
                          <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                            <p className="text-xs font-semibold text-emerald-700">ESIC — Auto</p>
                            <p className="text-xs text-emerald-600 mt-1">0.75% of Gross Salary</p>
                          </div>
                        )}
                        {formData.ded_ptax && (
                          <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                            <p className="text-xs font-semibold text-emerald-700">PTAX — Auto (Maharashtra)</p>
                            <p className="text-xs text-emerald-600 mt-1">
                              {formData.gender === 'Female' 
                                ? 'Female: Nil up to ₹25K, ₹200/m (Feb: ₹300)' 
                                : 'Male: Nil ≤₹7.5K, ₹175 ≤₹10K, ₹200 >₹10K (Feb: ₹300)'}
                            </p>
                          </div>
                        )}
                        {formData.ded_mlwf && (
                          <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                            <p className="text-xs font-semibold text-emerald-700">MLWF — Auto</p>
                            <p className="text-xs text-emerald-600 mt-1">Employee: ₹25, deducted in June & February only</p>
                          </div>
                        )}
                        {formData.ded_tds && (
                          <div>
                            <Label htmlFor="tds_percentage">TDS (%)</Label>
                            <Input id="tds_percentage" type="number" step="0.01" value={formData.tds_percentage} onChange={(e) => setFormData({ ...formData, tds_percentage: e.target.value })} placeholder="Enter TDS %" data-testid="tds-percentage-input" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Leave Tracking */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <div className="flex items-center space-x-2 mb-4">
                    <input
                      type="checkbox"
                      id="leave_tracking"
                      checked={formData.leave_tracking}
                      onChange={(e) => setFormData({ ...formData, leave_tracking: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300"
                      data-testid="leave-tracking-checkbox"
                    />
                    <Label htmlFor="leave_tracking" className="cursor-pointer font-semibold">
                      Apply Leave Quota (CL, SL, EL, Annual, Maternity/Paternity)
                    </Label>
                  </div>
                  
                  {formData.leave_tracking && (
                    <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div>
                        <Label htmlFor="casual_leave">Casual Leave (days/yr)</Label>
                        <Input id="casual_leave" type="number" value={formData.casual_leave} onChange={(e) => setFormData({ ...formData, casual_leave: e.target.value })} placeholder="0" data-testid="casual-leave-input" />
                      </div>
                      <div>
                        <Label htmlFor="sick_leave">Sick Leave (days/yr)</Label>
                        <Input id="sick_leave" type="number" value={formData.sick_leave} onChange={(e) => setFormData({ ...formData, sick_leave: e.target.value })} placeholder="0" data-testid="sick-leave-input" />
                      </div>
                      <div>
                        <Label htmlFor="earned_leave">Earned Leave (days/yr)</Label>
                        <Input id="earned_leave" type="number" value={formData.earned_leave} onChange={(e) => setFormData({ ...formData, earned_leave: e.target.value })} placeholder="0" data-testid="earned-leave-input" />
                      </div>
                      <div>
                        <Label htmlFor="annual_leave">Annual Leave (days/yr)</Label>
                        <Input id="annual_leave" type="number" value={formData.annual_leave} onChange={(e) => setFormData({ ...formData, annual_leave: e.target.value })} placeholder="0" data-testid="annual-leave-input" />
                      </div>
                      <div>
                        <Label htmlFor="maternity_paternity_leave">Maternity/Paternity (days)</Label>
                        <Input id="maternity_paternity_leave" type="number" value={formData.maternity_paternity_leave} onChange={(e) => setFormData({ ...formData, maternity_paternity_leave: e.target.value })} placeholder="0" data-testid="maternity-paternity-leave-input" />
                      </div>
                    </div>
                  )}
                </div>
                <Button type="submit" className="w-full" data-testid="submit-employee-button">
                  {editingEmployee ? 'Update Employee' : 'Create Employee'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end gap-4 flex-wrap">
        <div className="min-w-[200px]">
          <Label className="text-xs font-medium text-slate-600 mb-1 block">Filter by Name</Label>
          <select
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
            data-testid="filter-name-dropdown"
          >
            <option value="">All Employees</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.name}>{emp.name}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[180px]">
          <Label className="text-xs font-medium text-slate-600 mb-1 block">Filter by Designation</Label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
            data-testid="filter-type-dropdown"
          >
            <option value="">All Types</option>
            {employeeTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[180px]">
          <Label className="text-xs font-medium text-slate-600 mb-1 block">Filter by Cost Centre</Label>
          <select
            value={filterCostCentre}
            onChange={(e) => setFilterCostCentre(e.target.value)}
            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
            data-testid="filter-cost-centre-dropdown"
          >
            <option value="">All Cost Centres</option>
            {costCentres.map(cc => (
              <option key={cc.id} value={cc.id}>{cc.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600 mb-1 block">Filter by Emp Type</Label>
          <select
            value={filterEmpType}
            onChange={(e) => setFilterEmpType(e.target.value)}
            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
            data-testid="filter-emp-type-dropdown"
          >
            <option value="">All Types</option>
            {employmentTypeOptions.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        {(filterName || filterType || filterCostCentre || filterEmpType) && (
          <Button variant="outline" size="sm" onClick={() => { setFilterName(''); setFilterType(''); setFilterCostCentre(''); setFilterEmpType(''); }} data-testid="clear-filters-button">
            Clear Filters
          </Button>
        )}
      </div>

      {/* Employees Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Emp Code</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Designation</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Basic Salary</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const filteredEmployees = employees.filter(emp => {
                  if (filterName && emp.name !== filterName) return false;
                  if (filterType && emp.employee_type_id !== filterType) return false;
                  if (filterCostCentre && emp.cost_centre_id !== filterCostCentre) return false;
                  if (filterEmpType && (emp.employment_type || 'Payroll') !== filterEmpType) return false;
                  return true;
                });
                return filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-12 text-slate-500">
                    {employees.length === 0 ? 'No employees found. Add your first employee to get started.' : 'No employees match the selected filters.'}
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-mono font-semibold text-indigo-700">{employee.employee_code || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{(employee.name || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{employee.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{employee.phone}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{employee.employee_type_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        employee.employment_type === 'Commission' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-sky-50 text-sky-700 border border-sky-200'
                      }`}>{employee.employment_type || 'Payroll'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-900 text-right">
                      ₹{Math.round(parseFloat(employee.basic_salary)).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        employee.status === 'Active'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-slate-100 text-slate-700 border border-slate-200'
                      }`}>
                        {employee.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {user?.role !== 'CEO' && (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(employee)}
                          data-testid={`edit-employee-${employee.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(employee)}
                          data-testid={`delete-employee-${employee.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))
              );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Employees;
